import React, { useState } from "react";
import {
  Card,
  Typography,
  Checkbox,
  Alert,
  Space,
  Row,
  Col,
  Radio,
  Table,
  Tag,
} from "antd";

const { Title, Paragraph, Text } = Typography;

/* ---------------- PLAN DATA ---------------- */

const plans = [
  {
    key: "cx23",
    name: "CX23",
    vcpus: 2,
    ram: "4 GB",
    ssd: "40 GB",
    traffic: "20 TB",
    priceHour: "$0.006/h",
    priceMonth: "$3.49",
  },
  {
    key: "cx33",
    name: "CX33",
    vcpus: 4,
    ram: "8 GB",
    ssd: "80 GB",
    traffic: "20 TB",
    priceHour: "$0.010/h",
    priceMonth: "$5.99",
  },
  {
    key: "cx43",
    name: "CX43",
    vcpus: 8,
    ram: "16 GB",
    ssd: "80 GB",
    traffic: "20 TB",
    priceHour: "$0.016/h",
    priceMonth: "$9.99",
    highlight: true,
  },
];

/* ---------------- TABLE ---------------- */

const columns = [
  {
    title: "NAME",
    dataIndex: "name",
    render: (name) => (
      <>
        {name} <Tag color="gold">NEW</Tag>
      </>
    ),
  },
  { title: "VCPUS", dataIndex: "vcpus" },
  { title: "RAM", dataIndex: "ram" },
  { title: "SSD", dataIndex: "ssd" },
  { title: "TRAFFIC", dataIndex: "traffic" },
  { title: "PRICE", dataIndex: "priceHour" },
  {
    title: "PRICE / MONTH",
    dataIndex: "priceMonth",
    render: (v) => <Text type="danger">{v}</Text>,
  },
];

/* ---------------- COMPONENT ---------------- */

export default function Rescale() {
  const [cpuOnly, setCpuOnly] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("cx43");

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* HEADER */}
      <Card>
        <Title level={5}>RESCALE</Title>

        <Paragraph>
          You need more performance? Just scale up to a more powerful plan.
        </Paragraph>

        <Paragraph>
          You can rescale CPU and RAM only or expand the disk as well. A
          downgrade is only possible if the disk size stays unchanged.
        </Paragraph>

        <Paragraph>
          <Text strong>Your server needs to be powered off</Text> in order to be
          rescaled. The process usually takes just a few minutes.
        </Paragraph>

        <Checkbox
          checked={cpuOnly}
          onChange={(e) => setCpuOnly(e.target.checked)}
        >
          <Text strong>CPU and RAM only</Text>
          <br />
          <Text type="secondary">
            This leaves the disk size as is, which gives you the option to
            downgrade later.
          </Text>
        </Checkbox>
      </Card>

      {/* WARNING */}
      <Alert
        type="error"
        showIcon
        message="Rescaling is only possible to server types of the same architecture. Therefore, there is no option to change the architecture type."
      />

      {/* PLAN CATEGORIES */}
      <Row gutter={16}>
        <Col span={8}>
          <Card bordered>
            <Title level={5}>
              Cost-Optimized <Tag color="gold">Limited availability</Tag>
            </Title>
            <Text type="secondary">
              Cost-efficient cloud servers with limited availability. Ideal for
              variable workloads.
            </Text>
            <Paragraph>
              <Tag>Low CPU usage</Tag>
              <Tag>Medium traffic</Tag>
            </Paragraph>
            <Text strong>Architecture:</Text> x86 (Intel/AMD)
          </Card>
        </Col>

        <Col span={8}>
          <Card bordered>
            <Title level={5}>Regular Performance</Title>
            <Text type="secondary">
              Higher CPU performance based on newer hardware generations.
            </Text>
            <Paragraph>
              <Tag>Balanced performance</Tag>
              <Tag>Low to medium CPU</Tag>
            </Paragraph>
            <Text strong>Architecture:</Text> x86 (AMD)
          </Card>
        </Col>

        <Col span={8}>
          <Card bordered>
            <Title level={5}>General Purpose</Title>
            <Text type="secondary">
              Dedicated vCPUs with predictable performance for production
              workloads.
            </Text>
            <Paragraph>
              <Tag>High CPU usage</Tag>
              <Tag>Production</Tag>
            </Paragraph>
            <Text strong>Architecture:</Text> x86 (AMD)
          </Card>
        </Col>
      </Row>

      {/* PLAN SELECTION */}
      <Card>
        <Radio.Group
          value={selectedPlan}
          onChange={(e) => setSelectedPlan(e.target.value)}
          style={{ width: "100%" }}
        >
          <Table
            columns={columns}
            dataSource={plans}
            pagination={false}
            rowSelection={{
              type: "radio",
              selectedRowKeys: [selectedPlan],
              onChange: (keys) => setSelectedPlan(keys[0]),
            }}
            rowClassName={(record) =>
              record.key === selectedPlan ? "selected-row" : ""
            }
          />
        </Radio.Group>
      </Card>
    </Space>
  );
}
